/**
 * Our own backend's view of the signed-in user.
 *
 * Separate from `authService`, which only wraps Clerk: Clerk owns the identity
 * and issues the token, this asks Naurist's API who that token belongs to and
 * whether the account behind it is ready to use.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { AuthAccount, GetToken, WhoAmIResponse } from "../types/auth.types";

const toAccount = (response: WhoAmIResponse): AuthAccount => ({
  clerkUserId: response.clerk_user_id,
  email: response.email,
  fullName: response.full_name,
  emailVerified: response.email_verified,
  provisioned: response.provisioned,
});

/**
 * Resolves the current Clerk session against our backend.
 *
 * Only works once the session is active - `getToken()` returns null before
 * `setActive()` has resolved, and the endpoint is 401 without a bearer token.
 */
export async function fetchAccount(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<AuthAccount> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error("No active Clerk session - cannot call whoami.");
  }

  const response = await api.get<WhoAmIResponse>(API_ENDPOINTS.auth.whoami, {
    token,
    signal,
  });

  return toAccount(response);
}
